import { FC, useMemo, useState } from 'react';
import { EditableDataTable, EditableColumn } from '@/components/ui';
import { Search, Mail, MessageSquare, ExternalLink } from 'lucide-react';
import { SxmOutreach, OutreachChannel, OutreachResult } from '../types';
import { getOutreachResultColor, getOutreachResultLabel, formatDate } from '../utils/statusHelpers';
import { useCanEdit } from '@/hooks/useEditMode';

interface OutreachTabProps {
  outreach: SxmOutreach[];
  loading?: boolean;
  error?: string | null;
  userId?: string;
  pagination?: {
    pageIndex: number;
    pageSize: number;
    totalCount: number;
  };
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
}

export const OutreachTab: FC<OutreachTabProps> = ({
  outreach,
  loading,
  error,
  pagination,
  onPaginationChange
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [channelFilter, setChannelFilter] = useState<OutreachChannel | 'all'>('all');
  const [resultFilter, setResultFilter] = useState<OutreachResult | 'all'>('all');

  // Check if editing is enabled from centralized store
  const canEdit = useCanEdit();

  // Filter outreach based on search and filters
  const filteredOutreach = useMemo(() => {
    return outreach.filter(item => {
      const lead = item.lead;

      const matchesSearch = searchTerm === '' ||
        item.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead?.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead?.email?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesChannel = channelFilter === 'all' || item.channel === channelFilter;
      const matchesResult = resultFilter === 'all' || item.result === resultFilter;

      return matchesSearch && matchesChannel && matchesResult;
    });
  }, [outreach, searchTerm, channelFilter, resultFilter]);

  const getChannelIcon = (channel?: OutreachChannel) => {
    switch (channel) {
      case OutreachChannel.EMAIL:
        return <Mail className="w-4 h-4" />;
      case OutreachChannel.LINKEDIN:
        return <ExternalLink className="w-4 h-4" />;
      case OutreachChannel.WHATSAPP:
        return <MessageSquare className="w-4 h-4" />;
      default:
        return <Mail className="w-4 h-4" />;
    }
  };

  const getChannelLabel = (channel?: OutreachChannel) => {
    switch (channel) {
      case OutreachChannel.EMAIL:
        return 'Email';
      case OutreachChannel.LINKEDIN:
        return 'LinkedIn';
      case OutreachChannel.WHATSAPP:
        return 'WhatsApp';
      default:
        return 'Unknown';
    }
  };

  const truncateMessage = (message?: string, maxLength: number = 100) => {
    if (!message) return 'No message';
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  // Define columns for EditableDataTable
  const columns: EditableColumn<SxmOutreach>[] = [
    {
      key: 'leadId',
      header: 'Lead',
      editable: false,
      render: (item: SxmOutreach) => {
        const lead = item.lead;
        return (
          <div>
            <div className="text-sm font-medium text-gray-900">
              {lead?.fullName || 'Unknown Lead'}
            </div>
            <div className="text-sm text-gray-500">
              {lead?.companyName || 'No company'}
            </div>
            {lead?.email && (
              <div className="text-sm text-gray-500 mt-1">
                {lead.email}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'channel',
      header: 'Channel',
      editable: true,
      render: (item: SxmOutreach) => (
        <div className="flex items-center space-x-2">
          {getChannelIcon(item.channel)}
          <span className="text-sm text-gray-900">
            {getChannelLabel(item.channel)}
          </span>
        </div>
      ),
      EditComponent: ({ value, onChange }) => (
        <select
          value={value || OutreachChannel.EMAIL}
          onChange={(e) => onChange(e.target.value as OutreachChannel)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {Object.values(OutreachChannel).map(channel => (
            <option key={channel} value={channel}>
              {getChannelLabel(channel)}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: 'message',
      header: 'Message',
      editable: true,
      render: (item: SxmOutreach) => (
        <div className="text-sm text-gray-900 max-w-xs">
          {truncateMessage(item.message)}
        </div>
      ),
      validate: (value: string) => {
        if (!value?.trim()) return 'Message is required';
        return undefined;
      },
    },
    {
      key: 'result',
      header: 'Result',
      editable: true,
      render: (item: SxmOutreach) =>
        item.result ? (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getOutreachResultColor(item.result)}`}>
            {getOutreachResultLabel(item.result)}
          </span>
        ) : (
          <span className="text-sm text-gray-500">Pending</span>
        ),
      EditComponent: ({ value, onChange }) => (
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value ? e.target.value as OutreachResult : undefined)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Pending</option>
          {Object.values(OutreachResult).map(result => (
            <option key={result} value={result}>
              {getOutreachResultLabel(result)}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: 'sentAt',
      header: 'Sent',
      editable: false,
      render: (item: SxmOutreach) => (
        <div className="text-sm text-gray-900">
          {formatDate(item.sentAt)}
        </div>
      ),
    },
    {
      key: 'userId',
      header: 'Agent',
      editable: false,
      render: (item: SxmOutreach) => (
        <div className="text-sm text-gray-900">
          {item.userId || 'System'}
        </div>
      ),
    },
  ];

  // Handle row updates (though this component is primarily read-only)
  const handleRowUpdate = async (_oldRow: SxmOutreach, _newRow: SxmOutreach) => {
    // This would normally update the data via API
    // For now, we'll just log it since this is a display-only component
  };

  // Handle row deletion
  const handleRowDelete = async (_row: SxmOutreach) => {
    // This would normally delete the data via API
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Filters and Search */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search outreach by message, lead name, company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Channel Filter */}
          <div className="w-full sm:w-48">
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value as OutreachChannel | 'all')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Channels</option>
              {Object.values(OutreachChannel).map(channel => (
                <option key={channel} value={channel}>
                  {getChannelLabel(channel)}
                </option>
              ))}
            </select>
          </div>

          {/* Result Filter */}
          <div className="w-full sm:w-48">
            <select
              value={resultFilter}
              onChange={(e) => setResultFilter(e.target.value as OutreachResult | 'all')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Results</option>
              {Object.values(OutreachResult).map(result => (
                <option key={result} value={result}>
                  {getOutreachResultLabel(result)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
        <p className="text-sm text-gray-600">
          Showing {filteredOutreach.length} of {outreach.length} outreach records
        </p>
      </div>

      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
          {error}
        </div>
      )}

      {/* EditableDataTable */}
      <EditableDataTable
        columns={columns}
        data={filteredOutreach}
        loading={loading}
        keyExtractor={(item) => item.id.toString()}
        enableEditing={canEdit}
        enableDelete={false} // This is primarily a display component
        initialEditingMode="cell"
        onRowUpdate={handleRowUpdate}
        onRowDelete={handleRowDelete}
        onDataUpdate={() => { }} // No-op since this is display-only
        tableName="outreach_records"
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        manualPagination={!!onPaginationChange}
      />
    </div>
  );
};